import { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { useRefresh } from 'react-admin';
import { wsClient } from './WebSocketClient';
import type {
  WsMessagePayload,
  WsMessageUpdatedPayload,
  WsTypingPayload,
  WsReactionPayload,
  Message,
  AccessLevel,
  MessageStatusEvent,
} from '../types/chat.types';

/**
 * Typing user information
 */
export interface TypingUser {
  userId: number;
  email?: string;
  timestamp: number;
}

/**
 * Reaction information
 */
export interface MessageReaction {
  messageId: number;
  reaction: string;
  userId?: number;
  contactIdentityId?: number;
}

/**
 * useConversationSocket return type
 */
export interface UseConversationSocketReturn {
  /** Real-time messages received via WebSocket */
  messages: Message[];
  /** Users currently typing in the conversation */
  typing: TypingUser[];
  /** Whether subscribed to the conversation */
  isSubscribed: boolean;
  /** Error from subscription */
  error: Error | null;
  /** Access level for the conversation */
  accessLevel: AccessLevel | null;
  /** Reactions added/removed in real-time */
  reactions: Map<number, MessageReaction[]>;
  /** Clear all accumulated messages */
  clearMessages: () => void;
  /** Add a message (deduplicates against existing messages by id/uuid) */
  addMessage: (message: Message) => void;
}

/**
 * Hook to subscribe to conversation list updates.
 * Listens for new conversations created and updates the list in real-time.
 *
 * @param onConversationCreated - Callback when a new conversation is created
 * @returns Subscription status
 *
 * @example
 * ```tsx
 * function ConversationList() {
 *   const { refetch } = useGetList('conversations');
 *
 *   useConversationListSubscription({
 *     onConversationCreated: (conversation) => {
 *       console.log('New conversation:', conversation.uuid);
 *       refetch(); // Refresh the list
 *     },
 *   });
 *
 *   // ... render conversations
 * }
 * ```
 */
export function useConversationListSubscription(options: {
  onConversationCreated?: (conversation: any) => void;
} = {}) {
  const refresh = useRefresh();
  const { onConversationCreated } = options;

  useEffect(() => {
    const handleConversationCreated = (payload: { conversation: any }) => {
      console.log('[WebSocket] New conversation created:', payload.conversation?.uuid);

      // Call custom callback if provided
      onConversationCreated?.(payload.conversation);

      // Trigger React Admin refresh for conversations resource
      refresh();
    };

    wsClient.on('chat:conversation-created', handleConversationCreated);

    return () => {
      wsClient.off('chat:conversation-created', handleConversationCreated);
    };
  }, [refresh, onConversationCreated]);
}

/**
 * Hook for conversation WebSocket subscription.
 * Subscribes to a conversation on mount and listens for new messages,
 * typing indicators, and reactions in real-time.
 *
 * @param conversationId - ID of the conversation to subscribe to
 * @returns Subscription state with messages, typing users, and status
 *
 * @example
 * ```tsx
 * function ChatView({ conversationId }: { conversationId: number }) {
 *   const { messages, typing, isSubscribed, error } = useConversationSocket(conversationId);
 *
 *   if (error) return <div>Connection error: {error.message}</div>;
 *   if (!isSubscribed) return <div>Connecting...</div>;
 *
 *   return (
 *     <div>
 *       {messages.map((msg) => <MessageBubble key={msg.uuid} message={msg} />)}
 *       {typing.length > 0 && <TypingIndicator users={typing} />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useConversationSocket(conversationId: number | undefined): UseConversationSocketReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const [typing, setTyping] = useState<Map<number, TypingUser>>(new Map());
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [accessLevel, setAccessLevel] = useState<AccessLevel | null>(null);
  const [reactions, setReactions] = useState<Map<number, MessageReaction[]>>(new Map());
  const typingTimeoutRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

  // Clear messages when conversationId changes to prevent showing messages from previous chat
  useEffect(() => {
    setMessages([]);
    setTyping(new Map());
    setReactions(new Map());
    setIsSubscribed(false);
    setError(null);
    setAccessLevel(null);
    // Clear all typing timeouts
    typingTimeoutRef.current.forEach((timeout) => clearTimeout(timeout));
    typingTimeoutRef.current.clear();
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId) {
      setIsSubscribed(false);
      return;
    }

    const handleNewMessage = (payload: WsMessagePayload) => {
      if (payload.conversationId === conversationId) {
        setMessages((prev) => {
          // Dedup: if a message with the same id/uuid already exists (e.g. added
          // from API response), update it in place instead of appending
          const existingIdx = prev.findIndex(
            (m) => m.id === payload.message.id || (m.uuid && m.uuid === payload.message.uuid),
          );
          if (existingIdx !== -1) {
            const updated = [...prev];
            updated[existingIdx] = payload.message;
            return updated;
          }
          return [...prev, payload.message];
        });
      }
    };

    const handleMessageUpdated = (payload: WsMessageUpdatedPayload) => {
      if (payload.conversationId === conversationId) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === payload.messageId ? { ...msg, ...payload.updates } : msg
          )
        );
      }
    };

    const handleMessageDeleted = (payload: { conversationId: number; messageId: number }) => {
      if (payload.conversationId === conversationId) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === payload.messageId ? { ...msg, isDeleted: true } : msg
          )
        );
      }
    };

    const handleMessageStatus = (payload: MessageStatusEvent) => {
      if (payload.conversationId === conversationId) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === payload.messageId
              ? {
                  ...msg,
                  status: payload.status,
                  ...(payload.errorDetails !== undefined && { errorDetails: payload.errorDetails }),
                }
              : msg
          )
        );
      }
    };

    const handleTyping = (payload: WsTypingPayload) => {
      if (payload.conversationId === conversationId && payload.userId) {
        const userId = payload.userId;
        setTyping((prev) => {
          const next = new Map(prev);
          next.set(userId, {
            userId,
            email: payload.userEmail,
            timestamp: Date.now(),
          });
          return next;
        });

        // Clear existing timeout
        const existingTimeout = typingTimeoutRef.current.get(userId);
        if (existingTimeout) clearTimeout(existingTimeout);

        // Set timeout to remove typing indicator after 3 seconds
        const timeout = setTimeout(() => {
          setTyping((prev) => {
            const next = new Map(prev);
            next.delete(userId);
            return next;
          });
          typingTimeoutRef.current.delete(userId);
        }, 3000);
        typingTimeoutRef.current.set(userId, timeout);
      }
    };

    const handleStoppedTyping = (payload: WsTypingPayload) => {
      if (payload.conversationId === conversationId && payload.userId) {
        const userId = payload.userId;
        setTyping((prev) => {
          const next = new Map(prev);
          next.delete(userId);
          return next;
        });
        const timeout = typingTimeoutRef.current.get(userId);
        if (timeout) {
          clearTimeout(timeout);
          typingTimeoutRef.current.delete(userId);
        }
      }
    };

    const handleReactionAdded = (payload: WsReactionPayload) => {
      if (payload.conversationId === conversationId) {
        setReactions((prev) => {
          const next = new Map(prev);
          const existing = next.get(payload.messageId) || [];
          next.set(payload.messageId, [
            ...existing,
            {
              messageId: payload.messageId,
              reaction: payload.emoji,
              userId: payload.userId,
              contactIdentityId: payload.contactIdentityId,
            },
          ]);
          return next;
        });
      }
    };

    const handleReactionRemoved = (payload: WsReactionPayload) => {
      if (payload.conversationId === conversationId) {
        setReactions((prev) => {
          const next = new Map(prev);
          const existing = next.get(payload.messageId) || [];
          next.set(
            payload.messageId,
            existing.filter(
              (r) =>
                !(
                  r.reaction === payload.emoji &&
                  r.userId === payload.userId &&
                  r.contactIdentityId === payload.contactIdentityId
                )
            )
          );
          return next;
        });
      }
    };

    const handleSubscribed = (payload: { conversationId?: number; accessLevel: AccessLevel }) => {
      if (payload.conversationId === conversationId) {
        setIsSubscribed(true);
        setAccessLevel(payload.accessLevel);
        setError(null);
      }
    };

    const handleError = (err: Error) => {
      setError(err);
    };

    // Reset state on reconnect — WebSocketClient.resubscribeAll() already
    // re-emits the subscription before this callback fires, so we just need
    // to reset local state and wait for the fresh SUBSCRIBED event.
    const handleReconnect = () => {
      console.log('[useConversationSocket] Reconnected, resetting state for conversation', conversationId);
      setMessages([]);
      setTyping(new Map());
      setIsSubscribed(false);
      setAccessLevel(null);
    };

    wsClient.on('chat:new-message', handleNewMessage);
    wsClient.on('chat:message-updated', handleMessageUpdated);
    wsClient.on('chat:message-deleted', handleMessageDeleted);
    wsClient.on('chat:message-status', handleMessageStatus);
    wsClient.on('chat:user-typing', handleTyping);
    wsClient.on('chat:user-stopped-typing', handleStoppedTyping);
    wsClient.on('chat:reaction-added', handleReactionAdded);
    wsClient.on('chat:reaction-removed', handleReactionRemoved);
    wsClient.on('chat:subscribed', handleSubscribed);
    wsClient.on('error', handleError);
    wsClient.on('connect', handleReconnect);

    // Subscribe to the conversation
    wsClient.subscribeToConversation(conversationId);

    return () => {
      wsClient.off('chat:new-message', handleNewMessage);
      wsClient.off('chat:message-updated', handleMessageUpdated);
      wsClient.off('chat:message-deleted', handleMessageDeleted);
      wsClient.off('chat:message-status', handleMessageStatus);
      wsClient.off('chat:user-typing', handleTyping);
      wsClient.off('chat:user-stopped-typing', handleStoppedTyping);
      wsClient.off('chat:reaction-added', handleReactionAdded);
      wsClient.off('chat:reaction-removed', handleReactionRemoved);
      wsClient.off('chat:subscribed', handleSubscribed);
      wsClient.off('error', handleError);
      wsClient.off('connect', handleReconnect);

      // Unsubscribe from the conversation
      wsClient.unsubscribeFromConversation(conversationId);

      // Clear typing timeouts
      typingTimeoutRef.current.forEach((timeout) => clearTimeout(timeout));
      typingTimeoutRef.current.clear();

      setIsSubscribed(false);
    };
  }, [conversationId]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const addMessage = useCallback((message: Message) => {
    setMessages((prev) => {
      // Dedup: if WebSocket already delivered this message, update in place
      const existingIdx = prev.findIndex(
        (m) => m.id === message.id || (m.uuid && m.uuid === message.uuid),
      );
      if (existingIdx !== -1) {
        const updated = [...prev];
        updated[existingIdx] = message;
        return updated;
      }
      return [...prev, message];
    });
  }, []);

  // Convert typing Map to array for easier consumption
  const typingArray = useMemo(() => Array.from(typing.values()), [typing]);

  return {
    messages,
    typing: typingArray,
    isSubscribed,
    error,
    accessLevel,
    reactions,
    clearMessages,
    addMessage,
  };
}

/**
 * useTypingIndicator return type
 */
export interface UseTypingIndicatorReturn {
  /** Send a debounced typing event */
  sendTyping: () => void;
  /** Explicitly stop typing */
  stopTyping: () => void;
  /** List of users currently typing */
  typingUsers: TypingUser[];
  /** Whether the current user is typing */
  isTyping: boolean;
}

/**
 * Hook for typing indicator functionality.
 * Provides debounced typing events and tracks other users' typing status.
 *
 * @param conversationId - ID of the conversation
 * @returns Typing controls and status
 *
 * @example
 * ```tsx
 * function MessageInput({ conversationId }: { conversationId: number }) {
 *   const { sendTyping, stopTyping, typingUsers } = useTypingIndicatorEnhanced(conversationId);
 *
 *   return (
 *     <div>
 *       <textarea
 *         onChange={() => sendTyping()}
 *         onBlur={() => stopTyping()}
 *       />
 *       {typingUsers.length > 0 && (
 *         <span>{typingUsers.map(u => u.email).join(', ')} is typing...</span>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useTypingIndicatorEnhanced(conversationId: number | undefined): UseTypingIndicatorReturn {
  const [typingUsers, setTypingUsers] = useState<Map<number, TypingUser>>(new Map());
  const [isTyping, setIsTyping] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const typingTimeoutRef = useRef<Map<number, NodeJS.Timeout>>(new Map());
  const debounceDelay = 300;
  const typingDuration = 2000;

  useEffect(() => {
    if (!conversationId) return;

    const handleTyping = (payload: WsTypingPayload) => {
      if (payload.conversationId === conversationId && payload.userId) {
        const userId = payload.userId;
        setTypingUsers((prev) => {
          const next = new Map(prev);
          next.set(userId, {
            userId,
            email: payload.userEmail,
            timestamp: Date.now(),
          });
          return next;
        });

        // Clear existing timeout
        const existingTimeout = typingTimeoutRef.current.get(userId);
        if (existingTimeout) clearTimeout(existingTimeout);

        // Set timeout to remove typing indicator
        const timeout = setTimeout(() => {
          setTypingUsers((prev) => {
            const next = new Map(prev);
            next.delete(userId);
            return next;
          });
          typingTimeoutRef.current.delete(userId);
        }, 3000);
        typingTimeoutRef.current.set(userId, timeout);
      }
    };

    const handleStoppedTyping = (payload: WsTypingPayload) => {
      if (payload.conversationId === conversationId && payload.userId) {
        const userId = payload.userId;
        setTypingUsers((prev) => {
          const next = new Map(prev);
          next.delete(userId);
          return next;
        });
        const timeout = typingTimeoutRef.current.get(userId);
        if (timeout) {
          clearTimeout(timeout);
          typingTimeoutRef.current.delete(userId);
        }
      }
    };

    wsClient.on('chat:user-typing', handleTyping);
    wsClient.on('chat:user-stopped-typing', handleStoppedTyping);

    return () => {
      wsClient.off('chat:user-typing', handleTyping);
      wsClient.off('chat:user-stopped-typing', handleStoppedTyping);

      // Clear all timeouts
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      typingTimeoutRef.current.forEach((timeout) => clearTimeout(timeout));
      typingTimeoutRef.current.clear();
    };
  }, [conversationId]);

  const sendTyping = useCallback(() => {
    if (!conversationId) return;

    // Debounce: only send if not already typing
    if (!isTyping) {
      wsClient.startTyping(conversationId);
      setIsTyping(true);
    }

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set timeout to stop typing after inactivity
    timeoutRef.current = setTimeout(() => {
      if (conversationId) {
        wsClient.stopTyping(conversationId);
        setIsTyping(false);
      }
    }, typingDuration);
  }, [conversationId, isTyping]);

  const stopTyping = useCallback(() => {
    if (!conversationId) return;

    if (isTyping) {
      wsClient.stopTyping(conversationId);
      setIsTyping(false);
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [conversationId, isTyping]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (conversationId && isTyping) {
        wsClient.stopTyping(conversationId);
      }
    };
  }, [conversationId, isTyping]);

  const typingUsersArray = useMemo(() => Array.from(typingUsers.values()), [typingUsers]);

  return {
    sendTyping,
    stopTyping,
    typingUsers: typingUsersArray,
    isTyping,
  };
}
